import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import useAdminData from "../hooks/useAdminData";
import KnightLogo from "../components/site/KnightLogo";
import { LogOut, FileText, Mail, Users } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AgentDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("quotes");
  const { quotes, contacts, stats, fetching } = useAdminData(user, loading);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/agent/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/agent/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA]" data-testid="agent-dashboard">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <KnightLogo className="h-11 w-auto" tone="dark" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">
              {user.name || user.email}
            </span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-black/10 hover:bg-slate-50 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2]">
              Agent dashboard
            </span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight text-slate-900">
              Welcome back, <span className="font-serif-italic">{user.name?.split(" ")[0] || "Agent"}.</span>
            </h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <StatCard icon={FileText} label="Quote requests" value={stats.quotes} />
          <StatCard icon={Mail} label="Contact messages" value={stats.contacts} />
          <StatCard icon={Users} label="Total leads" value={(stats.quotes || 0) + (stats.contacts || 0)} />
        </div>

        {/* Tabs */}
        <div className="mt-10 inline-flex rounded-full border border-black/5 bg-white p-1" role="tablist">
          {["quotes", "contacts"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                tab === t ? "bg-[#011233] text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 rounded-3xl bg-white border border-black/5 overflow-hidden">
          {renderTable(fetching, tab, quotes, contacts)}
        </div>
      </main>
    </div>
  );
}

function renderTable(fetching, tab, quotes, contacts) {
  if (fetching) {
    return <div className="p-12 text-center text-slate-500">Loading…</div>;
  }
  if (tab === "quotes") {
    return <QuoteTable items={quotes} />;
  }
  return <ContactTable items={contacts} />;
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl bg-white border border-black/5 p-6 flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-light text-slate-900" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
      </div>
      <div className="w-12 h-12 rounded-2xl bg-[#011233]/5 text-[#011233] flex items-center justify-center">
        <Icon size={20} />
      </div>
    </div>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="p-16 text-center text-slate-500">
      No {label} yet. New submissions will appear here.
    </div>
  );
}

function QuoteTable({ items }) {
  if (!items.length) return <EmptyRow label="quote requests" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="quotes-table">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr>
            <Th>When</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Type</Th>
            <Th>Message</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((q) => (
            <tr key={q.id} className="border-t border-black/5 hover:bg-slate-50/50">
              <Td>{formatDate(q.created_at)}</Td>
              <Td className="font-medium text-slate-900">{q.name}</Td>
              <Td><a href={`mailto:${q.email}`} className="text-[#011233] hover:text-[#B5BAC2]">{q.email}</a></Td>
              <Td><a href={`tel:${q.phone}`} className="text-[#011233] hover:text-[#B5BAC2]">{q.phone}</a></Td>
              <Td>
                <span className="inline-flex px-2 py-1 rounded-full bg-[#B5BAC2]/10 text-[#011233] text-xs">
                  {q.insurance_type}
                </span>
              </Td>
              <Td className="max-w-md truncate text-slate-600">{q.message || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactTable({ items }) {
  if (!items.length) return <EmptyRow label="contact messages" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="contacts-table">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr>
            <Th>When</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Subject</Th>
            <Th>Message</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-black/5 hover:bg-slate-50/50">
              <Td>{formatDate(c.created_at)}</Td>
              <Td className="font-medium text-slate-900">{c.name}</Td>
              <Td><a href={`mailto:${c.email}`} className="text-[#011233] hover:text-[#B5BAC2]">{c.email}</a></Td>
              <Td>{c.subject}</Td>
              <Td className="max-w-md truncate text-slate-600">{c.message}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left font-medium px-5 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 align-top ${className}`}>{children}</td>;
